import axios from "axios";

// Create axios instance with base configuration
const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL ||
    "https://lead-generation-tool-be-258866255248.europe-west1.run.app",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    // Get access token from localStorage
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error);
    return Promise.reject(error);
  }
);

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map();

const getCacheKey = (url, params) => {
  return `${url}?${JSON.stringify(params || {})}`;
};

const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

// Clear cache function
export const clearCache = () => {
  cache.clear();
};

// Logs API
export const logsAPI = {
  // Get logs with filtering
  getLogs: async (filters = {}) => {
    const response = await api.get("/logs", { params: filters });
    return response.data;
  },

  // Get available services
  getServices: async () => {
    const response = await api.get("/logs/services");
    return response.data;
  },

  // Get log statistics
  getStats: async (filters = {}) => {
    const response = await api.get("/logs/stats", { params: filters });
    return response.data;
  },

  // Create test log
  createTestLog: async (logData) => {
    const response = await api.post("/logs/test", logData);
    return response.data;
  },

  // Stream logs using Server-Sent Events
  streamLogs: (filters = {}, onMessage, onError, onClose) => {
    const params = new URLSearchParams(filters);
    const eventSource = new EventSource(
      `${api.defaults.baseURL}/logs/stream?${params}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error("Error parsing SSE message:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE Error:", error);
      onError?.(error);
    };

    eventSource.onclose = () => {
      onClose?.();
    };

    return eventSource;
  },
};

// Job Batches API
export const jobBatchesAPI = {
  // Get all job batches with pagination
  getAll: async (page = 1, limit = 100) => {
    const userId = String(localStorage.getItem("userId") || "123");
    const cacheKey = getCacheKey(
      `/job-batches/job-batches/get-all-job-batches?user_id=${userId}`,
      { page }
    );
    const cached = getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await api.get(
      `/job-batches/job-batches/get-all-job-batches?user_id=${userId}`,
      { params: { page } }
    );
    const data = response.data;

    setCachedData(cacheKey, data);
    return data;
  },

  // Get job batch by ID
  getById: async (id) => {
    const cacheKey = getCacheKey(`/job-batches/${id}`);
    const cached = getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await api.get(
      `/job-batches/get-complete-batch-details/${id}`
    );
    const data = response.data;

    setCachedData(cacheKey, data);
    return data;
  },

  // Create new job batch
  create: async (jobBatchData) => {
    const response = await api.post("/job-batches/create-batch", jobBatchData);
    // Clear job batches cache
    cache.delete(getCacheKey("/job-batches"));
    return response.data;
  },

  // Update job batch
  update: async (id, jobBatchData) => {
    const response = await api.put(
      `/job-batches/update-batch/${id}`,
      jobBatchData
    );
    // Clear related caches
    cache.delete(getCacheKey("/job-batches"));
    cache.delete(getCacheKey(`/job-batches/${id}`));
    return response.data;
  },

  // Delete job batch
  delete: async (id) => {
    const response = await api.delete(`/job-batches/delete-batch/${id}`);
    // Clear related caches
    cache.delete(getCacheKey("/job-batches"));
    cache.delete(getCacheKey(`/job-batches/${id}`));
    return response.data;
  },
};

// Candidates API
export const candidatesAPI = {
  // Get all candidates with pagination
  getAll: async (filters = {}, page = 1, limit = 20) => {
    const cacheKey = getCacheKey("/candidates", { ...filters, page, limit });
    const cached = getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await api.get("/candidates", {
      params: { ...filters, page, limit },
    });
    const data = response.data;

    setCachedData(cacheKey, data);
    return data;
  },

  // Get candidate by ID
  getById: async (id) => {
    const cacheKey = getCacheKey(`/candidates/${id}`);
    const cached = getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await api.get(`/candidates/${id}`);
    const data = response.data;

    setCachedData(cacheKey, data);
    return data;
  },

  // Get candidates by batch with pagination
  getByBatch: async (batchId, filters = {}, page = 1, limit = 20) => {
    const userId = String(localStorage.getItem("userId") || "123");
    const cacheKey = getCacheKey(
      `/candidates/get-candidates-by-batch/${batchId}`,
      {
        ...filters,
        user_id: userId,
        limit,
      }
    );
    const cached = getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    // Use POST method with parameters in query string
    const response = await api.post(
      `/candidates/get-candidates-by-batch/${batchId}?user_id=${userId}&limit=${limit}`,
      {} // Empty body
    );
    const data = response.data;
    setCachedData(cacheKey, data);
    return data;
  },

  // Save candidate
  save: async (candidateData) => {
    const response = await api.post(
      "/candidates/save-candidate",
      candidateData
    );
    // Clear candidates cache
    cache.delete(getCacheKey("/candidates"));
    return response.data;
  },

  // Delete candidate
  delete: async (linkedinUrl) => {
    const response = await api.delete(
      `/candidates/delete-candidate/${encodeURIComponent(linkedinUrl)}`
    );
    // Clear candidates cache
    cache.delete(getCacheKey("/candidates"));
    return response.data;
  },

  // Clear all candidates
  clearAll: async () => {
    const response = await api.delete("/candidates/clear-all");
    // Clear candidates cache
    cache.delete(getCacheKey("/candidates"));
    return response.data;
  },
};

// Keywords API
export const keywordsAPI = {
  // Get keywords for batch
  getByBatch: async (batchId) => {
    const response = await api.get(`/keywords/batch/${batchId}`);
    return response.data;
  },

  // Save keywords
  save: async (batchId, iteration, keywords) => {
    const response = await api.post("/keywords/save", {
      batch_id: batchId,
      iteration,
      keywords,
    });
    return response.data;
  },
};

// Harvest API
export const harvestAPI = {
  // Start harvest pipeline
  startHarvest: async (harvestData) => {
    const response = await api.post("/harvest/start", harvestData);
    return response.data;
  },

  // Get pipeline status
  getStatus: async (pipelineId) => {
    const response = await api.get(`/harvest/status/${pipelineId}`);
    return response.data;
  },

  // Get candidate sourcing status
  getCandidateSourcingStatus: async (userId, batchId) => {
    const response = await api.get(
      `/candidate-sourcing/status?user_id=${userId}&batch_id=${batchId}`
    );
    return response.data;
  },

  // Stop pipeline
  stopPipeline: async (pipelineId) => {
    const response = await api.post("/harvest/stop", {
      pipeline_id: pipelineId,
    });
    return response.data;
  },

  // Extract keywords
  extractKeywords: async (keywordData) => {
    const response = await api.post("/harvest/extract-keywords", keywordData);
    return response.data;
  },

  // Start pipeline

  // Get stats
  getStats: async () => {
    const response = await api.get("/harvest/stats");
    return response.data;
  },
};

// Export API
export const exportAPI = {
  // Export batch to CSV
  exportBatch: async (batchId, filepath) => {
    const response = await api.post("/export/batch", {
      batch_id: batchId,
      filepath,
    });
    return response.data;
  },

  // Export all to CSV
  exportAll: async (filepath) => {
    const response = await api.post("/export/all", { filepath });
    return response.data;
  },
};

// Configuration API
export const configAPI = {
  // Get configuration
  get: async () => {
    const response = await api.get("/configuration");
    return response.data;
  },

  // Update configuration
  update: async (configData) => {
    const response = await api.put("/configuration", configData);
    return response.data;
  },
};

// Google Connection API
export const googleAPI = {
  // Test connection
  testConnection: async () => {
    const response = await api.get("/google/test-connection");
    return response.data;
  },

  // Get document
  getDocument: async (documentId) => {
    const response = await api.get(`/google/document/${documentId}`);
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  const response = await api.get("/health");
  return response.data;
};

// Authentication API
export const authAPI = {
  // Signup/Register user
  signup: async (email, password) => {
    const response = await api.post("/auth/signup", {
      email,
      password,
    });
    return response.data;
  },

  // Login user
  login: async (email, password) => {
    const response = await api.post("/auth/signin", {
      email,
      password,
    });
    return response.data;
  },

  // Logout user
  logout: async () => {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      throw new Error("No tokens found for logout");
    }

    const response = await api.post(
      `/auth/logout?access_token=${encodeURIComponent(
        accessToken
      )}&refresh_token=${encodeURIComponent(refreshToken)}`
    );
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },
};

// Google Maps API - Use same baseURL as Lead Generation API
const googleMapsApi = axios.create({
  baseURL: "https://lead-generation-tool-be-258866255248.europe-west1.run.app",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false,
});

export const googleMapsAPI = {
  // Search companies using Google Maps - POST /google-maps/search
  search: async (payload) => {
    try {
      console.log(
        "Searching with Google Maps API:",
        JSON.stringify(payload, null, 2)
      );
      const response = await googleMapsApi.post("/google-maps/search", payload);
      console.log("Google Maps API response:", response.data);
      // Return full response object
      return response.data;
    } catch (error) {
      console.error("Google Maps API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },

  // Get job details - GET /google-maps/job-details/{job_id}
  getJobDetails: async (jobId) => {
    try {
      console.log("Fetching job details for job_id:", jobId);
      const response = await googleMapsApi.get(
        `/google-maps/job-details/${jobId}`
      );
      console.log("Job details API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Job Details API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },

  // Get jobs list - GET /google-maps/jobs
  getJobs: async () => {
    try {
      // Use localhost URL as specified
      const localApi = axios.create({
        baseURL:
          "https://lead-generation-tool-be-258866255248.europe-west1.run.app",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const response = await localApi.get("/google-maps/jobs");
      console.log("Google Maps Jobs API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Google Maps Jobs API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },

  // Get jobs history - GET /google-maps/jobs-history?job_id={job_id}&google_maps=true
  getJobsHistory: async (jobId) => {
    try {
      console.log("Fetching jobs history for job_id:", jobId);
      const response = await googleMapsApi.get("/google-maps/jobs-history", {
        params: {
          job_id: jobId,
          google_maps: true,
        },
      });
      console.log("Jobs History API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Jobs History API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },
};

// Lead Generation API
const leadGenApi = axios.create({
  baseURL: "https://lead-generation-tool-be-258866255248.europe-west1.run.app",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false,
});

export const leadGenAPI = {
  // Initiate pipeline - POST /lead-gen/initiate-pipeline
  initiatePipeline: async (payload) => {
    try {
      console.log("Sending payload:", JSON.stringify(payload, null, 2));
      const response = await leadGenApi.post(
        "/lead-gen/initiate-pipeline",
        payload
      );
      console.log("Pipeline response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Pipeline API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      });
      throw error;
    }
  },

  // Search companies - POST /lead-gen/search-companies
  searchCompanies: async (payload) => {
    try {
      console.log(
        "Searching companies with payload:",
        JSON.stringify(payload, null, 2)
      );
      const response = await leadGenApi.post(
        "/lead-gen/search-companies",
        payload
      );
      console.log("Companies response:", response.data);
      // Response is an array directly, not wrapped in {companies: []}
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Companies API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      // If 404, provide helpful error message
      if (error.response?.status === 404) {
        const errorMsg = new Error(
          `Endpoint not found: ${error.config?.method?.toUpperCase()} ${
            error.config?.url
          }. Please verify the endpoint exists on the backend.`
        );
        errorMsg.originalError = error;
        throw errorMsg;
      }
      throw error;
    }
  },

  // Search employees - POST /lead-gen/search-employees
  searchEmployees: async (payload) => {
    try {
      console.log(
        "Searching employees with payload:",
        JSON.stringify(payload, null, 2)
      );
      const response = await leadGenApi.post(
        "/lead-gen/search-employees",
        payload
      );
      console.log("Employees response:", response.data);
      // Response is an array directly, not wrapped in {employees: []}
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Employees API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Search people using Apollo - POST /apollo/people-search
  searchPeople: async (payload) => {
    try {
      console.log(
        "Searching people with Apollo API payload:",
        JSON.stringify(payload, null, 2)
      );
      const response = await leadGenApi.post("/apollo/people-search", payload);
      console.log("Apollo people search response:", response.data);
      // Response format may vary, return the data as is
      return response.data;
    } catch (error) {
      console.error("Apollo People Search API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },

  // Shortlist decision makers - POST /apollo/shortlisted-descion-makers
  shortlistDecisionMakers: async (apolloIds) => {
    try {
      console.log(
        "Shortlisting decision makers with Apollo IDs:",
        JSON.stringify(apolloIds, null, 2)
      );
      const response = await leadGenApi.post(
        "/apollo/shortlisted-descion-makers",
        {
          apollo_ids: apolloIds,
        }
      );
      console.log("Shortlist decision makers response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Shortlist Decision Makers API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  },

  // Legacy methods for backward compatibility (deprecated - use searchCompanies/searchEmployees instead)
  getCompaniesByJob: async (jobId) => {
    console.warn(
      "getCompaniesByJob is deprecated. Use searchCompanies instead."
    );
    // This method is kept for backward compatibility but should not be used
    throw new Error(
      "This method is deprecated. Please use searchCompanies instead."
    );
  },

  getEmployeesByCompanies: async (companyIds, jobId) => {
    console.warn(
      "getEmployeesByCompanies is deprecated. Use searchEmployees instead."
    );
    // This method is kept for backward compatibility but should not be used
    throw new Error(
      "This method is deprecated. Please use searchEmployees instead."
    );
  },
};

// LinkedIn Mappings
// Reverse mapping: ID -> Standard text format
export const HEADCOUNT_ID_TO_TEXT = {
  A: "Self-employed",
  B: "1-10",
  C: "11-50",
  D: "51-200",
  E: "201-500",
  F: "501-1,000",
  G: "1,001-5,000",
  H: "5,001-10,000",
  I: "10,001+",
};

// Reverse mapping: Text -> ID
export const HEADCOUNT_TEXT_TO_ID = {
  "Self-employed": "A",
  "1-10": "B",
  "11-50": "C",
  "51-200": "D",
  "201-500": "E",
  "501-1,000": "F",
  "1,001-5,000": "G",
  "5,001-10,000": "H",
  "10,001+": "I",
};

// LinkedIn Sales Navigator industry name to ID mapping
// Industry IDs extracted from actual LinkedIn Sales Navigator URLs
export const INDUSTRY_NAME_TO_ID = {
  // Industries with verified IDs from URLs
  "Space Research and Technology": "3089",
  "Food and Beverage Manufacturing": "23",
  Retail: "27",
  Restaurants: "32",
  Restauraunts: "32", // Common misspelling
  "Travel Arrangements": "30",
  Hospitality: "31",
  "Artists and Writers": "38",
  Banking: "41",
  Insurance: "42",
  Accounting: "47",
  Construction: "48",
  "Architecture and Planning": "50",
  "Architecture & Planning": "50", // Alternative format
  "Civil Engineering": "51",
  Mining: "56",
  "Textile Manufacturing": "60",
  "Blockchain Services": "3134",
  "Sports Teams and Clubs": "2142",
  "Staffing and Recruiting": "104",
  Cosmetics: "3189",
  "Import & Export": "3209",
  "Graphic Design": "140",
};

// LinkedIn API functions
export const linkedinAPI = {
  // Generate LinkedIn message - POST /linkedin/generate-message
  generateMessage: async (prompt) => {
    try {
      const response = await api.post("/linkedin/generate-message", {
        prompt: prompt,
      });
      return response.data;
    } catch (error) {
      console.error("Generate Message API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Send LinkedIn message - POST /linkedin/send-message
  sendMessage: async (profileUrls, message) => {
    try {
      const response = await api.post("/linkedin/send-message", {
        profile_urls: Array.isArray(profileUrls) ? profileUrls : [profileUrls],
        message: message,
      });
      return response.data;
    } catch (error) {
      console.error("Send Message API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Save email - POST /linkedin/save-email
  saveEmail: async (email, source = "") => {
    try {
      const response = await api.post("/linkedin/save-email", {
        email: email,
        source: source,
      });
      return response.data;
    } catch (error) {
      console.error("Save Email API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // LinkedIn search - POST /linkedin-search/search
  search: async (
    searchName,
    query,
    jobId,
    numberOfCompanies,
    wantPhoneNumber = false
  ) => {
    try {
      const response = await api.post("/linkedin-search/search", {
        search_name: searchName,
        query: query,
        job_id: jobId,
        number_of_companies: numberOfCompanies,
        phone_number: wantPhoneNumber,
      });
      return response.data;
    } catch (error) {
      console.error("LinkedIn Search API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get LinkedIn search status - GET /linkedin-search/status/{job_id}
  getStatus: async (jobId) => {
    try {
      const response = await api.get(`/linkedin-search/status/${jobId}`);
      return response.data;
    } catch (error) {
      console.error("Get LinkedIn Status API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get LinkedIn companies - GET /linkedin-search/companies?job_id=...
  getCompanies: async (jobId) => {
    try {
      const response = await api.get("/linkedin-search/companies", {
        params: {
          job_id: jobId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Get LinkedIn Companies API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get LinkedIn jobs - GET /linkedin-search/jobs
  getJobs: async () => {
    try {
      const response = await api.get("/linkedin-search/jobs");
      console.log("LinkedIn Jobs API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Get LinkedIn Jobs API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Apollo people search for LinkedIn - GET /linkedin-search/apollo-people-search
  apolloPeopleSearch: async (jobId, searchName) => {
    try {
      const response = await api.get("/linkedin-search/apollo-people-search", {
        params: {
          job_id: jobId,
          search_name: searchName,
        },
      });
      console.log("LinkedIn Apollo People Search API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("LinkedIn Apollo People Search API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get LinkedIn job history - GET /linkedin-search/jobs-history?job_id={job_id}
  getJobsHistory: async (jobId) => {
    try {
      const response = await api.get("/linkedin-search/jobs-history", {
        params: {
          job_id: jobId,
        },
      });
      console.log("LinkedIn Jobs History API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("LinkedIn Jobs History API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
};

// Voice Agent API functions
export const voiceAgentAPI = {
  // Get all calls - GET /call-get-logs
  getCalls: async () => {
    try {
      const response = await api.get("/call-get-logs");
      console.log("Get Calls API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Get Calls API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
  // Make a call - POST /make-call
  makeCall: async (phoneNumber, personId, organisationId) => {
    try {
      const response = await api.post("/make-call", {
        phone_number: phoneNumber,
        person_id: personId,
        organisation_id: organisationId,
      });
      return response.data;
    } catch (error) {
      console.error("Make Call API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
};

// Smartlead API functions
export const smartleadAPI = {
  // Create campaign - POST /smartlead/campaigns/create
  createCampaign: async (employeeIds, campaignName = "") => {
    try {
      const response = await api.post("/smartlead/campaigns/create", {
        employee_ids: employeeIds,
        campaign_name: campaignName,
      });
      return response.data;
    } catch (error) {
      console.error("Create Campaign API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Update email outreach status - PUT /smartlead/outreaches/status
  updateOutreachStatus: async (outreachId, status) => {
    try {
      const response = await api.put("/smartlead/outreaches/status", {
        outreach_id: outreachId,
        status: status,
      });
      return response.data;
    } catch (error) {
      console.error("Update Outreach Status API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get campaign statistics - GET /smartlead/campaigns/stats
  getCampaignStats: async (campaignId = null) => {
    try {
      const params = campaignId ? { campaign_id: campaignId } : {};
      const response = await api.get("/smartlead/campaigns/stats", { params });
      return response.data;
    } catch (error) {
      console.error("Get Campaign Stats API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get email outreaches by status - GET /smartlead/outreaches/by-status
  getOutreachesByStatus: async (status, campaignId = null) => {
    try {
      const params = { status };
      if (campaignId) {
        params.campaign_id = campaignId;
      }
      const response = await api.get("/smartlead/outreaches/by-status", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Get Outreaches By Status API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Update campaign status - PUT /smartlead/campaigns/status
  updateCampaignStatus: async (campaignId, status) => {
    try {
      const response = await api.put("/smartlead/campaigns/status", {
        campaign_id: campaignId,
        status: status,
      });
      return response.data;
    } catch (error) {
      console.error("Update Campaign Status API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // Get outreaches by status - GET /outreaches/by-status
  getOutreachesByStatusAll: async () => {
    try {
      const response = await api.get("/smartlead/outreaches/by-status");
      return response.data;
    } catch (error) {
      console.error("Get Outreaches By Status API Error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
};

export default api;
